// PeekHost — the global entity-peek host for PROJECT-scoped entities (ADR-036
// §2). Reads the open peek from the URL (`?peek=<collection>:<id>`,
// useEntityPeek) and renders the matching entity's peek. So ANY page opens a
// peek by setting the param via openPeek — no page imports or renders a peek
// component itself. Mounted once in each project frame — AdminLayout ·
// ProjectLayout · the Assets and Work area layouts (ADR-036 — the domain frame hosts
// too, so the sites peek rides here like every other). The platform frame has its own
// sibling (PlatformPeekHost — platform-tier entities, no project context).
//
// The host OWNS THE SHEET (`PeekSheet` — the overlay: entrance, focus, Escape,
// modality-follows-width): one stays mounted for as long as `?peek=` is set, and
// the entity's peek renders only the panel's face inside it. So the record
// arriving, a step to another row and a link to another kind of record swap the
// face in place — the sheet never replays its entrance (DESIGN-TOKENS §Motion),
// whatever shape an entity's peek takes.
//
// Dispatch is by collection prefix — roles · zones · clusters ·
// administrative-areas · geo-country · contract-configs · users · sites ·
// assets · asset-models · tickets · teams · audit (one audit line). A new
// entity adds a branch; if the chain keeps growing, graduate it to a
// registration map keyed by collection (mirroring the nav/entity registries).

import type { ReactNode } from 'react';
import { useEntityPeek } from '../../hooks/useEntityPeek.js';
import { PeekSheet } from '../patterns/ContextPanel.js';
import { ConfigPeek } from '../../pages/contract/ConfigPeek.js';
import { RolePeek } from '../../pages/roles/RolePeek.js';
import { ZonePeek } from '../../pages/admin/zones/ZonePeek.js';
import { ClusterPeek } from '../../pages/admin/clusters/ClusterPeek.js';
import { AdministrativeAreaPeek } from '../../pages/admin/administrative-areas/AdministrativeAreaPeek.js';
import { UserPeek } from '../../pages/users/UserPeek.js';
import { SitePeek } from '../../pages/sites/SitePeek.js';
import { AssetPeek } from '../../pages/assets/AssetPeek.js';
import { AssetModelPeek } from '../../pages/catalog/AssetModelPeek.js';
import { TicketPeek } from '../../pages/tickets/TicketPeek.js';
import { TeamPeek } from '../../pages/teams/TeamPeek.js';
import { AuditEventPeek } from '../../pages/admin/audit/AuditEventPeek.js';

/** A positive-integer id, or null when the raw segment isn't one. */
function intId(raw: string): number | null {
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : null;
}

export function PeekHost({ projectId }: { projectId: string }) {
  const { peek, closePeek } = useEntityPeek();
  const face = peek ? peekFace(peek, projectId, closePeek) : null;
  return (
    <PeekSheet open={face !== null} onClose={closePeek}>
      {face}
    </PeekSheet>
  );
}

/** The open ref's face — the entity's peek — or null for a ref no entity answers. */
function peekFace(peek: string, projectId: string, closePeek: () => void): ReactNode {
  const sep = peek.indexOf(':');
  if (sep < 0) return null;
  const collection = peek.slice(0, sep);
  const rawId = peek.slice(sep + 1);

  if (collection === 'roles') {
    const roleId = intId(rawId);
    return roleId === null ? null : <RolePeek projectId={projectId} roleId={roleId} onClose={closePeek} />;
  }

  if (collection === 'zones') {
    const zoneId = intId(rawId);
    return zoneId === null ? null : <ZonePeek projectId={projectId} zoneId={zoneId} onClose={closePeek} />;
  }

  if (collection === 'clusters') {
    const clusterId = intId(rawId);
    return clusterId === null ? null : <ClusterPeek projectId={projectId} clusterId={clusterId} onClose={closePeek} />;
  }

  // Both administrativeAreas-tree targets ride ONE AdministrativeAreaPeek instance so country↔node
  // navigation keeps the same face — see AdministrativeAreaPeek header.
  if (collection === 'administrative-areas') {
    const administrativeAreaId = intId(rawId);
    return administrativeAreaId === null ? null : (
      <AdministrativeAreaPeek projectId={projectId} target={{ kind: 'node', id: administrativeAreaId }} onClose={closePeek} />
    );
  }
  // A COUNTRY (the administrativeAreas tree's top level) — rawId is the ISO code (string).
  if (collection === 'geo-country') {
    if (!rawId) return null;
    return <AdministrativeAreaPeek projectId={projectId} target={{ kind: 'country', iso: rawId }} onClose={closePeek} />;
  }

  // Contractual configurations — the Contract surface's peek.
  if (collection === 'contract-configs') {
    const configId = intId(rawId);
    return configId === null ? null : <ConfigPeek projectId={projectId} configId={configId} onClose={closePeek} />;
  }

  // Catalog products carry STRING ids (amdl_ nanoid) — pass rawId through.
  if (collection === 'asset-models') {
    if (!rawId) return null;
    return <AssetModelPeek projectId={projectId} modelId={rawId} onClose={closePeek} />;
  }

  // Users carry STRING ids (nanoid) — pass rawId through, no integer parse.
  if (collection === 'users') {
    if (!rawId) return null;
    return <UserPeek projectId={projectId} userId={rawId} onClose={closePeek} />;
  }

  // Sites carry STRING ids (nanoid). The peek reads its Registry/Solution face
  // from the list's `?view=` itself (SitePeek header note).
  if (collection === 'sites') {
    if (!rawId) return null;
    return <SitePeek projectId={projectId} siteId={rawId} onClose={closePeek} />;
  }

  // Assets carry STRING ids. ONE peek, both list faces — it reads Register/Books
  // from `?view=` itself (the SitePeek pattern).
  if (collection === 'assets') {
    if (!rawId) return null;
    return <AssetPeek projectId={projectId} assetId={rawId} onClose={closePeek} />;
  }

  // Tickets carry STRING ids (nanoid) — peek-only (no full page); the hub's walk steps
  // this one panel from row to row.
  if (collection === 'tickets') {
    if (!rawId) return null;
    return <TicketPeek projectId={projectId} ticketId={rawId} onClose={closePeek} />;
  }

  // Teams carry STRING ids (`team_`) — the Teams list's row peek (ADR-083 §11).
  if (collection === 'teams') {
    if (!rawId) return null;
    return <TeamPeek projectId={projectId} teamId={rawId} onClose={closePeek} />;
  }

  // One audit line (the trail's shareable peek) — its id is the bigint identity, digits.
  if (collection === 'audit') {
    return /^[1-9]\d{0,17}$/.test(rawId) ? <AuditEventPeek projectId={projectId} eventId={rawId} onClose={closePeek} /> : null;
  }

  // Unknown collection — render nothing.
  return null;
}
